import { useState } from 'react';
import ExcelJS from 'exceljs';
import { useFinance, Transaction } from '@/context/FinanceContext';
import { z } from 'zod';

interface ImportResult {
  success: boolean;
  message: string;
  count?: number;
  errors?: string[];
}

interface TransactionImportData {
  description: string;
  value: number;
  categoryName?: string;
  accountName?: string;
  paymentMethodName?: string;
  costCenterName?: string;
  supplierName?: string;
  issueDate: string | number | Date;
  dueDate: string | number | Date;
  paymentDate?: string | number | Date;
}

interface CategoryImportData {
  name: string;
  type?: 'receita' | 'despesa';
  dreCategory?: string;
  color?: string;
}

interface AccountImportData {
  name: string;
  initialBalance?: number;
  color?: string;
}

interface SupplierImportData {
  name: string;
}

interface PaymentMethodImportData {
  name: string;
}

interface CostCenterImportData {
  name: string;
}

// Validation schemas
const transactionSchema = z.object({
  // Descrição agora é opcional
  description: z.string().trim().max(500, 'Descrição deve ter no máximo 500 caracteres').optional().default(''),
  // Valor obrigatório
  value: z.number()
    .refine(val => !isNaN(val) && isFinite(val), 'Valor inválido')
    .refine(val => val !== 0, 'Valor não pode ser zero')
    .refine(val => Math.abs(val) <= 999999999, 'Valor muito alto'),
  // Campos de lookup agora são obrigatórios
  categoryName: z.string().trim().min(1, 'Plano de Contas é obrigatório').max(200),
  accountName: z.string().trim().min(1, 'Conta Financeira é obrigatória').max(200),
  paymentMethodName: z.string().trim().min(1, 'Forma de Pagamento é obrigatória').max(200),
  costCenterName: z.string().trim().min(1, 'Centro de Custo é obrigatório').max(200),
  supplierName: z.string().trim().min(1, 'Fornecedor é obrigatório').max(200),
  // Datas obrigatórias
  issueDate: z.union([
    z.string().min(1, 'Data de Emissão é obrigatória'),
    z.number().min(1, 'Data de Emissão é obrigatória'),
    z.date()
  ]),
  dueDate: z.union([
    z.string().min(1, 'Data de Vencimento é obrigatória'),
    z.number().min(1, 'Data de Vencimento é obrigatória'),
    z.date()
  ]),
  // Data de pagamento opcional - em branco = aberto, com data = pago
  paymentDate: z.union([z.string(), z.number(), z.date()]).optional()
});

const categorySchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(200, 'Nome deve ter no máximo 200 caracteres'),
  type: z.enum(['receita', 'despesa']),
  dreCategory: z.string().max(200).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida').optional()
});

const accountSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(200, 'Nome deve ter no máximo 200 caracteres'),
  initialBalance: z.number().max(999999999, 'Saldo muito alto').finite('Saldo inválido').optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida').optional()
});

const supplierSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(200, 'Nome deve ter no máximo 200 caracteres')
});

const paymentMethodSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(200, 'Nome deve ter no máximo 200 caracteres')
});

const costCenterSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(200, 'Nome deve ter no máximo 200 caracteres')
});

// Sem limite de linhas para importação

export const useImport = () => {
  const [importing, setImporting] = useState(false);
  const { 
    addTransaction, 
    bulkAddTransactions,
    categories, 
    accounts, 
    paymentMethods, 
    costCenters, 
    suppliers,
    addCategory,
    addAccount,
    addSupplier,
    addPaymentMethod,
    addCostCenter
  } = useFinance();

  // Helper function to convert Brazilian date format (DD/MM/YYYY), Excel serial date, or Date object to ISO format (YYYY-MM-DD)
  const parseBrazilianDate = (dateStr: string | number | Date): string => {
    if (!dateStr && dateStr !== 0) return '';
    
    // Se for um objeto Date do JavaScript (retornado pelo ExcelJS)
    if (dateStr instanceof Date) {
      if (!isNaN(dateStr.getTime())) {
        const year = dateStr.getUTCFullYear();
        const month = String(dateStr.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateStr.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    }
    
    // Se for um número (serial date do Excel) - fallback caso cellDates não funcione
    if (typeof dateStr === 'number') {
      // Excel stores dates as days since 1900-01-01 (with 1900 bug)
      const excelEpoch = new Date(1899, 11, 30); // 30 Dec 1899
      const date = new Date(excelEpoch.getTime() + dateStr * 86400000);
      
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    }
    
    // Converter para string se não for
    const dateString = String(dateStr).trim();
    if (!dateString) return '';
    
    // Check if it's already in ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse Brazilian format (DD/MM/YYYY) - exatamente como o usuário digita
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      // Validate date ranges
      if (year < 1900 || year > 2100) {
        console.warn(`Data inválida - ano fora do range: ${year}`);
        return '';
      }
      if (month < 1 || month > 12) {
        console.warn(`Data inválida - mês fora do range: ${month}`);
        return '';
      }
      if (day < 1 || day > 31) {
        console.warn(`Data inválida - dia fora do range: ${day}`);
        return '';
      }
      
      // Validate actual date - USAR CONSTRUTOR COM PARÂMETROS (evita problemas de timezone)
      const date = new Date(year, month - 1, day); // month é 0-indexed no JavaScript
      
      if (!isNaN(date.getTime()) && 
          date.getFullYear() === year && 
          date.getMonth() + 1 === month && 
          date.getDate() === day) {
        // Format to ISO string
        const dayStr = String(day).padStart(2, '0');
        const monthStr = String(month).padStart(2, '0');
        const yearStr = String(year);
        return `${yearStr}-${monthStr}-${dayStr}`;
      } else {
        console.warn(`Data inválida - não forma uma data real: ${dateString}`);
      }
    }
    
    return '';
  };

  // Helper to extract cell value properly
  const getCellValue = (cell: ExcelJS.Cell): any => {
    if (cell.value === null || cell.value === undefined) return undefined;
    
    // Handle rich text
    if (typeof cell.value === 'object' && 'richText' in cell.value) {
      return (cell.value as ExcelJS.CellRichTextValue).richText.map(rt => rt.text).join('');
    }
    
    // Handle formula results
    if (typeof cell.value === 'object' && 'result' in cell.value) {
      return (cell.value as ExcelJS.CellFormulaValue).result;
    }
    
    // Handle hyperlinks
    if (typeof cell.value === 'object' && 'hyperlink' in cell.value) {
      return (cell.value as ExcelJS.CellHyperlinkValue).text;
    }
    
    return cell.value;
  };

  const readExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(data);
          
          const worksheet = workbook.worksheets[0];
          if (!worksheet) {
            reject(new Error('Planilha não encontrada no arquivo'));
            return;
          }

          const rows: any[] = [];
          let headers: string[] = [];
          let isFirstRow = true;
          
          worksheet.eachRow((row, rowNumber) => {
            if (isFirstRow) {
              // First row is header
              isFirstRow = false;
              const rowValues = row.values as any[];
              headers = rowValues.slice(1).map((v, i) => {
                const cell = row.getCell(i + 1);
                const value = getCellValue(cell);
                return String(value || '');
              });
            } else {
              // Data rows
              const obj: any = {};
              headers.forEach((header, index) => {
                const cell = row.getCell(index + 1);
                obj[header] = getCellValue(cell);
              });
              rows.push(obj);
            }
          });
          
          if (rows.length < 1) {
            reject(new Error('Arquivo deve conter pelo menos uma linha de cabeçalho e uma linha de dados'));
            return;
          }

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
      reader.readAsArrayBuffer(file);
    });
  };

  const importTransactions = async (file: File): Promise<ImportResult> => {
    try {
      setImporting(true);
      const data = await readExcelFile(file);
      const totalRowsRead = data.length;

      const errors: string[] = [];
      let successCount = 0;
      const validTransactions: Parameters<typeof addTransaction>[0][] = [];

      // Sets para coletar valores únicos não cadastrados
      const missingCategories = new Set<string>();
      const missingAccounts = new Set<string>();
      const missingPaymentMethods = new Set<string>();
      const missingCostCenters = new Set<string>();
      const missingSuppliers = new Set<string>();

      // Helper para interpretar coluna "Pago" (opcional): sim/não/true/false/1/0/x
      const parsePagoFlag = (raw: any): boolean | null => {
        if (raw === null || raw === undefined || raw === '') return null;
        if (typeof raw === 'boolean') return raw;
        if (typeof raw === 'number') return raw !== 0;
        const v = String(raw).trim().toLowerCase();
        if (!v) return null;
        if (['sim', 's', 'true', 'verdadeiro', '1', 'x', 'pago', 'yes', 'y'].includes(v)) return true;
        if (['não', 'nao', 'n', 'false', 'falso', '0', 'aberto', 'pendente', 'no'].includes(v)) return false;
        return null;
      };

      for (const [index, row] of data.entries()) {
        const rowNum = index + 2;
        
        try {
          const transactionData: TransactionImportData = {
            description: row['Descrição'] || row['description'] || row['Description'] || '',
            value: parseFloat(row['Valor'] || row['value'] || row['Value'] || '0'),
            categoryName: (row['Plano de Contas'] || row['Categoria'] || row['category'] || row['Category'] || '').toString().trim(),
            accountName: (row['Conta Financeira'] || row['Conta'] || row['account'] || row['Account'] || '').toString().trim(),
            paymentMethodName: (row['Forma de Pagamento'] || row['paymentMethod'] || row['Payment Method'] || '').toString().trim(),
            costCenterName: (row['Centro Custo'] || row['Centro de Custo'] || row['costCenter'] || row['Cost Center'] || '').toString().trim(),
            supplierName: (row['Fornecedor'] || row['supplier'] || row['Supplier'] || '').toString().trim(),
            issueDate: row['Emissão'] || row['Data Emissão'] || row['issueDate'] || row['Issue Date'] || '',
            dueDate: row['Vencimento'] || row['Data Vencimento'] || row['dueDate'] || row['Due Date'] || '',
            paymentDate: row['Pagamento'] || row['Data Pagamento'] || row['paymentDate'] || row['Payment Date'] || ''
          };

          const pagoFlag = parsePagoFlag(row['Pago'] ?? row['pago'] ?? row['Status'] ?? row['status']);

          try {
            transactionSchema.parse(transactionData);
          } catch (validationError) {
            if (validationError instanceof z.ZodError) {
              const allErrors = validationError.errors.map(e => e.message).join('; ');
              errors.push(`Linha ${rowNum}: ${allErrors}`);
            }
            continue;
          }

          const category = categories.find(c => c.name.toLowerCase() === transactionData.categoryName?.toLowerCase());
          const account = accounts.find(a => a.name.toLowerCase() === transactionData.accountName?.toLowerCase());
          const paymentMethod = paymentMethods.find(pm => pm.name.toLowerCase() === transactionData.paymentMethodName?.toLowerCase());
          const costCenter = costCenters.find(cc => cc.name.toLowerCase() === transactionData.costCenterName?.toLowerCase());
          const supplier = suppliers.find(s => s.name.toLowerCase() === transactionData.supplierName?.toLowerCase());

          const notFoundFields: string[] = [];
          if (!category) { notFoundFields.push(`Plano de Contas: "${transactionData.categoryName}"`); missingCategories.add(transactionData.categoryName || ''); }
          if (!account) { notFoundFields.push(`Conta Financeira: "${transactionData.accountName}"`); missingAccounts.add(transactionData.accountName || ''); }
          if (!paymentMethod) { notFoundFields.push(`Forma de Pagamento: "${transactionData.paymentMethodName}"`); missingPaymentMethods.add(transactionData.paymentMethodName || ''); }
          if (!costCenter) { notFoundFields.push(`Centro de Custo: "${transactionData.costCenterName}"`); missingCostCenters.add(transactionData.costCenterName || ''); }
          if (!supplier) { notFoundFields.push(`Fornecedor: "${transactionData.supplierName}"`); missingSuppliers.add(transactionData.supplierName || ''); }

          if (notFoundFields.length > 0) {
            errors.push(`Linha ${rowNum}: Campos não cadastrados - ${notFoundFields.join('; ')}`);
            continue;
          }

          const parsedIssueDate = parseBrazilianDate(transactionData.issueDate);
          const parsedDueDate = parseBrazilianDate(transactionData.dueDate);
          const parsedPaymentDate = parseBrazilianDate(transactionData.paymentDate || '');

          if (!parsedIssueDate) {
            errors.push(`Linha ${rowNum}: Data de Emissão inválida ou não informada. Valor recebido: "${transactionData.issueDate}". Use o formato DD/MM/YYYY`);
            continue;
          }
          if (!parsedDueDate) {
            errors.push(`Linha ${rowNum}: Data de Vencimento inválida ou não informada. Valor recebido: "${transactionData.dueDate}". Use o formato DD/MM/YYYY`);
            continue;
          }

          const transactionType: 'receita' | 'despesa' = transactionData.value >= 0 ? 'receita' : 'despesa';

          if (category.type && category.type !== transactionType) {
            const tipoValor = transactionType === 'receita' ? 'positivo (receita)' : 'negativo (despesa)';
            const tipoConta = category.type === 'receita' ? 'Receita' : 'Despesa';
            errors.push(`Linha ${rowNum}: Valor ${tipoValor} incompatível com Plano de Contas "${category.name}" cadastrado como ${tipoConta}`);
            continue;
          }

          const absoluteValue = Math.abs(transactionData.value);

          // Status (settled):
          // 1) Se coluna "Pago" estiver preenchida, ela manda.
          // 2) Caso contrário, mantém regra antiga: data de pagamento preenchida = pago.
          const isSettled = pagoFlag !== null ? pagoFlag : parsedPaymentDate !== '';

          validTransactions.push({
            description: transactionData.description,
            value: absoluteValue,
            type: transactionType,
            categoryId: category.id,
            accountId: account.id,
            paymentMethodId: paymentMethod.id,
            costCenterId: costCenter.id,
            supplierId: supplier.id,
            issueDate: parsedIssueDate,
            dueDate: parsedDueDate,
            paymentDate: parsedPaymentDate,
            settled: isSettled,
            kind: 'normal'
          });
        } catch (error) {
          errors.push(`Linha ${rowNum}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      const summaryErrors: string[] = [];
      if (missingCategories.size > 0) summaryErrors.push(`⚠️ Planos de Contas não cadastrados: ${Array.from(missingCategories).join(', ')}`);
      if (missingAccounts.size > 0) summaryErrors.push(`⚠️ Contas Financeiras não cadastradas: ${Array.from(missingAccounts).join(', ')}`);
      if (missingPaymentMethods.size > 0) summaryErrors.push(`⚠️ Formas de Pagamento não cadastradas: ${Array.from(missingPaymentMethods).join(', ')}`);
      if (missingCostCenters.size > 0) summaryErrors.push(`⚠️ Centros de Custo não cadastrados: ${Array.from(missingCostCenters).join(', ')}`);
      if (missingSuppliers.size > 0) summaryErrors.push(`⚠️ Fornecedores não cadastrados: ${Array.from(missingSuppliers).join(', ')}`);

      const totalErrors = errors.length;
      if (summaryErrors.length > 0) {
        errors.unshift('--- RESUMO DOS CADASTROS FALTANTES ---', ...summaryErrors, '--- ERROS POR LINHA ---');
      }

      // Tudo-ou-nada: grava só se 100% das linhas passaram na validação
      let bulkErrors: string[] = [];
      let bulkFailed = 0;
      if (totalErrors === 0 && validTransactions.length > 0) {
        try {
          const result = await bulkAddTransactions(validTransactions);
          successCount = result.inserted;
          bulkFailed = result.failed;
          bulkErrors = result.errors;
        } catch (e: any) {
          bulkFailed = validTransactions.length;
          bulkErrors = [e?.message || 'Erro ao gravar lançamentos'];
        }
      }

      const allOk =
        totalErrors === 0 &&
        bulkFailed === 0 &&
        successCount === validTransactions.length &&
        validTransactions.length === totalRowsRead;

      const baseMsg =
        `Lidas: ${totalRowsRead} | Válidas: ${validTransactions.length} | Importadas: ${successCount}` +
        (bulkFailed > 0 ? ` | Falhas: ${bulkFailed}` : '') +
        (totalErrors > 0 ? ` | Descartadas: ${totalErrors}` : '');

      let message: string;
      if (allOk) {
        message = `✅ ${baseMsg}. Todos os lançamentos foram importados — saldo da conta será atualizado.`;
      } else if (totalErrors > 0) {
        message = `❌ Nenhum lançamento foi importado (regra "tudo ou nada"). ${baseMsg}. Corrija os erros abaixo e tente novamente.`;
      } else if (bulkFailed > 0) {
        message = `❌ Falha na gravação. ${baseMsg}. Foi feito rollback — nenhum dado parcial ficou no banco. ${bulkErrors[0] || ''}`.trim();
      } else {
        message = `⚠️ ${baseMsg}.`;
      }

      return {
        success: allOk,
        message,
        count: successCount,
        errors: [...errors, ...bulkErrors]
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    } finally {
      setImporting(false);
    }
  };


  const importCategories = async (file: File): Promise<ImportResult> => {
    try {
      setImporting(true);
      const data = await readExcelFile(file);
      
      // Sem limite de linhas - processamento ilimitado
      
      const errors: string[] = [];
      let successCount = 0;

      for (const [index, row] of data.entries()) {
        const rowNum = index + 2;
        
        try {
          const categoryData: CategoryImportData = {
            name: row['Nome'] || row['name'] || row['Name'] || '',
            type: (row['Tipo'] || row['type'] || row['Type'] || 'despesa').toLowerCase() as 'receita' | 'despesa',
            dreCategory: row['Categoria DRE'] || row['dreCategory'] || row['DRE Category'] || '',
            color: row['Cor'] || row['color'] || row['Color'] || '#64748B'
          };

          // Validate with Zod schema
          try {
            categorySchema.parse(categoryData);
          } catch (validationError) {
            if (validationError instanceof z.ZodError) {
              errors.push(`Linha ${rowNum}: ${validationError.errors[0].message}`);
            }
            continue;
          }

          // Check if category already exists
          const existingCategory = categories.find(c => c.name === categoryData.name);
          if (existingCategory) {
            errors.push(`Linha ${rowNum}: Categoria "${categoryData.name}" já existe`);
            continue;
          }

          addCategory(
            categoryData.name,
            categoryData.color,
            categoryData.type,
            categoryData.dreCategory
          );

          successCount++;
        } catch (error) {
          errors.push(`Linha ${rowNum}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      return {
        success: successCount > 0,
        message: `${successCount} categorias importadas com sucesso${errors.length > 0 ? ` (${errors.length} erros)` : ''}`,
        count: successCount,
        errors
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    } finally {
      setImporting(false);
    }
  };

  const importAccounts = async (file: File): Promise<ImportResult> => {
    try {
      setImporting(true);
      const data = await readExcelFile(file);
      
      // Sem limite de linhas - processamento ilimitado
      
      const errors: string[] = [];
      let successCount = 0;

      for (const [index, row] of data.entries()) {
        const rowNum = index + 2;
        
        try {
          const accountData: AccountImportData = {
            name: row['Nome'] || row['name'] || row['Name'] || '',
            initialBalance: parseFloat(row['Saldo Inicial'] || row['initialBalance'] || row['Initial Balance'] || '0'),
            color: row['Cor'] || row['color'] || row['Color'] || '#64748B'
          };

          // Validate with Zod schema
          try {
            accountSchema.parse(accountData);
          } catch (validationError) {
            if (validationError instanceof z.ZodError) {
              errors.push(`Linha ${rowNum}: ${validationError.errors[0].message}`);
            }
            continue;
          }

          // Check if account already exists
          const existingAccount = accounts.find(a => a.name === accountData.name);
          if (existingAccount) {
            errors.push(`Linha ${rowNum}: Conta "${accountData.name}" já existe`);
            continue;
          }

          addAccount(
            accountData.name,
            accountData.color,
            accountData.initialBalance || 0
          );

          successCount++;
        } catch (error) {
          errors.push(`Linha ${rowNum}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      return {
        success: successCount > 0,
        message: `${successCount} contas importadas com sucesso${errors.length > 0 ? ` (${errors.length} erros)` : ''}`,
        count: successCount,
        errors
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    } finally {
      setImporting(false);
    }
  };

  const importSuppliers = async (file: File): Promise<ImportResult> => {
    try {
      setImporting(true);
      const data = await readExcelFile(file);
      
      // Sem limite de linhas - processamento ilimitado
      
      const errors: string[] = [];
      let successCount = 0;

      for (const [index, row] of data.entries()) {
        const rowNum = index + 2;
        
        try {
          const supplierData: SupplierImportData = {
            name: row['Nome'] || row['name'] || row['Name'] || ''
          };

          // Validate with Zod schema
          try {
            supplierSchema.parse(supplierData);
          } catch (validationError) {
            if (validationError instanceof z.ZodError) {
              errors.push(`Linha ${rowNum}: ${validationError.errors[0].message}`);
            }
            continue;
          }

          // Check if supplier already exists
          const existingSupplier = suppliers.find(s => s.name === supplierData.name);
          if (existingSupplier) {
            errors.push(`Linha ${rowNum}: Fornecedor "${supplierData.name}" já existe`);
            continue;
          }

          addSupplier(supplierData.name);

          successCount++;
        } catch (error) {
          errors.push(`Linha ${rowNum}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      return {
        success: successCount > 0,
        message: `${successCount} fornecedores importados com sucesso${errors.length > 0 ? ` (${errors.length} erros)` : ''}`,
        count: successCount,
        errors
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    } finally {
      setImporting(false);
    }
  };

  const importPaymentMethods = async (file: File): Promise<ImportResult> => {
    try {
      setImporting(true);
      const data = await readExcelFile(file);
      
      // Sem limite de linhas - processamento ilimitado
      
      const errors: string[] = [];
      let successCount = 0;

      for (const [index, row] of data.entries()) {
        const rowNum = index + 2;
        
        try {
          const paymentMethodData: PaymentMethodImportData = {
            name: row['Nome'] || row['name'] || row['Name'] || ''
          };

          // Validate with Zod schema
          try {
            paymentMethodSchema.parse(paymentMethodData);
          } catch (validationError) {
            if (validationError instanceof z.ZodError) {
              errors.push(`Linha ${rowNum}: ${validationError.errors[0].message}`);
            }
            continue;
          }

          // Check if payment method already exists
          const existingPaymentMethod = paymentMethods.find(pm => pm.name === paymentMethodData.name);
          if (existingPaymentMethod) {
            errors.push(`Linha ${rowNum}: Forma de pagamento "${paymentMethodData.name}" já existe`);
            continue;
          }

          addPaymentMethod(paymentMethodData.name);

          successCount++;
        } catch (error) {
          errors.push(`Linha ${rowNum}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      return {
        success: successCount > 0,
        message: `${successCount} formas de pagamento importadas com sucesso${errors.length > 0 ? ` (${errors.length} erros)` : ''}`,
        count: successCount,
        errors
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    } finally {
      setImporting(false);
    }
  };

  const importCostCenters = async (file: File): Promise<ImportResult> => {
    try {
      setImporting(true);
      const data = await readExcelFile(file);
      
      // Sem limite de linhas - processamento ilimitado
      
      const errors: string[] = [];
      let successCount = 0;

      for (const [index, row] of data.entries()) {
        const rowNum = index + 2;
        
        try {
          const costCenterData: CostCenterImportData = {
            name: row['Nome'] || row['name'] || row['Name'] || ''
          };

          // Validate with Zod schema
          try {
            costCenterSchema.parse(costCenterData);
          } catch (validationError) {
            if (validationError instanceof z.ZodError) {
              errors.push(`Linha ${rowNum}: ${validationError.errors[0].message}`);
            }
            continue;
          }

          // Check if cost center already exists
          const existingCostCenter = costCenters.find(cc => cc.name === costCenterData.name);
          if (existingCostCenter) {
            errors.push(`Linha ${rowNum}: Centro de custo "${costCenterData.name}" já existe`);
            continue;
          }

          addCostCenter(costCenterData.name);

          successCount++;
        } catch (error) {
          errors.push(`Linha ${rowNum}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      }

      return {
        success: successCount > 0,
        message: `${successCount} centros de custo importados com sucesso${errors.length > 0 ? ` (${errors.length} erros)` : ''}`,
        count: successCount,
        errors
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    } finally {
      setImporting(false);
    }
  };

  return {
    importing,
    importTransactions,
    importCategories,
    importAccounts,
    importSuppliers,
    importPaymentMethods,
    importCostCenters
  };
};
